import React, { memo, useEffect, useState, useRef, useLayoutEffect, useCallback, useReducer } from "react";
import axios from "axios";
import KickChatPost from "./KickChatPost";
import { useKickAuth, useKickChannelIdentity, KICK_SCOPES, kickModerationLoginUrl } from "./providers/Auth/KickProvider";
import { kickChatReducer, initialKickChatState, isUserBanned, isUserTimedOut, lastDeletableMessageId } from "./kick/kickEventReducer";
import { useKickStream } from "./kick/useKickStream";
import { useKickChannel } from "./kick/useKickChannel";

import { log } from './lib/logger';
import KickFeedItem, { kickFeedItemKey } from './kick/KickFeedItem';
import KickUserPanel from './kick/KickUserPanel';
import KickConnectionBanner from './kick/KickConnectionBanner';

const KickChatComponent = memo(({ username, active }) => {
  // Configuration: Max age for history (in hours)
  const HISTORY_MAX_AGE_MS = parseInt(
    process.env.REACT_APP_HISTORY_MAX_AGE_HOURS || '2', 
    10
  ) * 60 * 60 * 1000; // Default 2 hours
  
  // Configuration: Max messages to keep in memory
  const MAX_MESSAGES = parseInt(
    process.env.REACT_APP_MAX_MESSAGES || '100',
    10
  ); // Default 100 messages

  // Feed state (messages, bans/timeouts, the viewer's own restriction) lives in a pure reducer
  const [chat, dispatch] = useReducer(kickChatReducer, initialKickChatState);
  const { messages, recentChatters, viewerRestriction } = chat;
  const { broadcasterId, channelId, subBadges: channelSubBadges } = useKickChannel(username);
  const [showNewMessages, setShowNewMessages] = useState(false);
  const [showConnectionStatus, setShowConnectionStatus] = useState(false); // Delay showing status
  const [replyingTo, setReplyingTo] = useState(null);
  // The logged-in viewer comes from KickProvider; their badges in this channel from useKickChannelIdentity
  const { user, hasScope, checkAuthStatus } = useKickAuth();
  // Moderation scopes are opt-in (see kickModerationLoginUrl), so being a moderator isn't enough
  const canBanScope = hasScope(KICK_SCOPES.ban);
  const canDeleteScope = hasScope(KICK_SCOPES.deleteMessage);
  const currentUserId = user?.id ?? null; // string
  const currentUsername = user?.username ?? null;
  const { identity: currentUserIdentity, isLoading: identityLoading } = useKickChannelIdentity(channelId);
  const [selectedUser, setSelectedUser] = useState(null); // Track which user panel is open (replaces showModActions)

  const scrollRef = useRef(null);
  const userScrolled = useRef(false);
  const initialLoad = useRef(true);
  // Close the user overview panel - used for moderation UI
  const closeUserPanel = useCallback(() => {
    setSelectedUser(null);
  }, []);

  // ⭐ MODERATION: Check if current user is authenticated
  const isAuthenticated = useCallback(() => {
    return currentUserId !== null && currentUsername !== null && currentUserIdentity !== null;
  }, [currentUserId, currentUsername, currentUserIdentity]);

  // ⭐ MODERATION: Check if current user is a moderator for this channel
  const isModeratorForChannel = useCallback(() => {
    if (!isAuthenticated()) return false;
    return currentUserIdentity?.badges?.some(
      badge => badge.type === 'moderator' && badge.active === true
    );
  }, [isAuthenticated, currentUserIdentity]);

  // ⭐ MODERATION: Check if current user is the broadcaster
  const isBroadcasterForChannel = useCallback(() => {
    if (!isAuthenticated()) return false;
    const hasBroadcaster = currentUserIdentity?.badges?.some(
      badge => badge.type === 'broadcaster' && badge.active === true
    );
    const isOwner = broadcasterId && String(currentUserId) === String(broadcasterId);
    return hasBroadcaster && isOwner;
  }, [isAuthenticated, currentUserIdentity, currentUserId, broadcasterId]);

  // ⭐ MODERATION: Check if current user can moderate
  const canModerate = useCallback(() => {
    return isModeratorForChannel() || isBroadcasterForChannel();
  }, [isModeratorForChannel, isBroadcasterForChannel]);

  // Helper function to check if user should see moderation system messages
  const canSeeModerationMessages = useCallback(() => {
    return canModerate();
  }, [canModerate]);

  // ⭐ MODERATION: Can the viewer moderate the sender of message m? Not themselves, and the
  // broadcaster only if the viewer is the broadcaster.
  const canModerateSender = (m) => {
    const isOwnMessage = currentUserId && String(m.sender?.id || '') === currentUserId;
    return !!(isAuthenticated() && canModerate() && !isOwnMessage && (String(m.sender?.id) !== String(broadcasterId) || isBroadcasterForChannel()));
  };

  // ⭐ MODERATION: Handle moderation actions
  const handleModAction = useCallback(async (action, targetUserId, messageId, duration = null) => {
    // ⭐ CRITICAL: Check authentication FIRST
    if (!isAuthenticated()) {
      console.error('[Moderation] ❌ Not authenticated');
      alert('You must be logged in to perform moderation actions');
      return;
    }

    // ⭐ CRITICAL: Check moderation permission
    if (!canModerate()) {
      console.error('[Moderation] ❌ Not authorized');
      alert('You do not have permission to moderate this channel');
      return;
    }

    // ⭐ CRITICAL: Verify we have broadcaster ID
    if (!broadcasterId) {
      console.error('[Moderation] ❌ Broadcaster ID not available');
      alert('Unable to perform moderation action: Channel information not loaded');
      return;
    }

    if (action === 'delete' && !messageId) {
      console.error('[Moderation] ❌ No message to delete');
      return;
    }

    // ⭐ SECURITY: Prevent moderating the broadcaster (only broadcaster can moderate themselves)
    if (String(targetUserId) === String(broadcasterId) && !isBroadcasterForChannel()) {
      console.error('[Moderation] ❌ Cannot moderate the broadcaster');
      alert('You cannot moderate the channel owner');
      return;
    }

    // ⭐ SECURITY: Prevent moderating yourself (unless you're testing)
    if (String(targetUserId) === String(currentUserId) && process.env.NODE_ENV !== 'development') {
      console.error('[Moderation] ❌ Cannot moderate yourself');
      alert('You cannot moderate yourself');
      return;
    }

    try {
      let endpoint;
      let method;
      let payload;
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // In development, pass dev token via header
      if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_KICK_DEV_ACCESS_TOKEN) {
        headers['X-Dev-Token'] = process.env.REACT_APP_KICK_DEV_ACCESS_TOKEN;
      }

      // Route all moderation actions through kick-send service
      const serviceUrl = process.env.REACT_APP_KICK_CHAT_SEND_SERVICE;

      switch (action) {
        case 'timeout':
          endpoint = `${serviceUrl}/moderation/timeout`;
          method = 'POST';
          payload = {
            broadcaster_user_id: broadcasterId,
            user_id: targetUserId,
            duration: duration || 10
          };
          break;
          
        case 'ban':
          endpoint = `${serviceUrl}/moderation/ban`;
          method = 'POST';
          payload = {
            broadcaster_user_id: broadcasterId,
            user_id: targetUserId
          };
          break;
          
        case 'unban':
          endpoint = `${serviceUrl}/moderation/unban`;
          method = 'DELETE';
          payload = {
            broadcaster_user_id: broadcasterId,
            user_id: targetUserId
          };
          break;
          
        case 'untimeout':
          // Remove timeout uses same endpoint as unban
          endpoint = `${serviceUrl}/moderation/unban`;
          method = 'DELETE';
          payload = {
            broadcaster_user_id: broadcasterId,
            user_id: targetUserId
          };
          break;
          
        case 'delete':
          endpoint = `${serviceUrl}/moderation/message/${encodeURIComponent(messageId)}`;
          method = 'DELETE';
          payload = null;
          break;
          
        default:
          console.error('[Moderation] Unknown action:', action);
          return;
      }

      log.debug('[Moderation] Making request:', { action, method, endpoint, payload });

      const response = await axios({
        method,
        url: endpoint,
        ...(payload && { data: payload }),
        headers,
        withCredentials: true // Send cookies for session auth
      });

      log.debug(`[Moderation] ${action} successful:`, response.data);
      
      // Optimistic local update; kick-chat-service also broadcasts the resulting moderation event
      dispatch({ type: 'moderated', action, userId: targetUserId, messageId, durationMinutes: duration, now: Date.now() });
      
      // Close the user panel after action
      closeUserPanel();
      
    } catch (error) {
      console.error(`[Moderation] Failed to ${action}:`, error);
      console.error('[Moderation] Error details:', error.response?.data);
      // Kick refused: the login may lack the scope or have ended, so refresh what we know about it
      if (error.response?.data?.code === 'KICK_UNAUTHORIZED') checkAuthStatus();
      alert(`Failed to ${action} ${action === 'delete' ? 'message' : 'user'}: ${error.response?.data?.error || error.response?.data?.message || error.message}`);
    }
  }, [isAuthenticated, canModerate, broadcasterId, closeUserPanel, currentUserId, isBroadcasterForChannel, checkAuthStatus]);

  // ⭐ MODERATION: Toggle mod actions menu
  // Handle clicking on a username to open user overview panel
  const handleUserClick = useCallback((user) => {
    // ⭐ CRITICAL: Check if user is authenticated
    if (!isAuthenticated()) {
      log.debug('[Moderation] ❌ User not authenticated, cannot open moderation panel');
      return;
    }

    // ⭐ CRITICAL: Check if user has moderation permission
    if (!canModerate()) {
      log.debug('[Moderation] ❌ User does not have moderation permission');
      return;
    }
    
    // Don't allow clicking on own username
    if (!user.sender?.id || (currentUserId && String(user.sender.id) === currentUserId)) {
      log.debug('[Moderation] ❌ Cannot moderate yourself');
      return;
    }
    
    // Don't allow moderators to moderate the broadcaster
    if (String(user.sender.id) === String(broadcasterId) && !isBroadcasterForChannel()) {
      log.debug('[Moderation] ❌ Cannot moderate the broadcaster');
      return;
    }
    
    log.debug('[Moderation] ✅ Opening moderation panel for user:', user.sender.username);
    
    setSelectedUser({
      id: user.sender.id,
      username: user.sender.username,
      color: user.sender.identity?.color || '#fff',
      badges: user.sender.identity?.badges || [],
      isBanned: isUserBanned(chat, user.sender.id),
      isTimedOut: isUserTimedOut(chat, user.sender.id)
    });
  }, [isAuthenticated, canModerate, currentUserId, broadcasterId, isBroadcasterForChannel, chat]);

  // Viewer context the reducer needs per event, read through a ref so the stream never reconnects for it
  const viewerRef = useRef({});
  viewerRef.current = { viewerId: currentUserId, canModerate: canModerate() };

  const chatService = process.env.REACT_APP_KICK_CHAT_SERVICE;
  const streamUrl = username && chatService ? `${chatService}/stream?channel=${encodeURIComponent(username)}` : null;
  const { status: connectionStatus, reconnect } = useKickStream(streamUrl, {
    onConnecting: () => dispatch({ type: 'connecting' }),
    onHeartbeat: () => dispatch({ type: 'heartbeat' }),
    onEvent: (data) => dispatch({
      type: 'event',
      data,
      now: Date.now(),
      maxAgeMs: HISTORY_MAX_AGE_MS,
      maxMessages: MAX_MESSAGES,
      ...viewerRef.current,
    }),
  });

  // Delay showing connection status on initial load
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowConnectionStatus(true);
    }, 2000); // 2 second delay

    return () => clearTimeout(timer);
  }, []);

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const atBottom = scrollHeight - scrollTop - clientHeight < 50;
    userScrolled.current = !atBottom;
    if (atBottom) setShowNewMessages(false);
  };

  const scrollToBottom = () => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    userScrolled.current = false;
    setShowNewMessages(false);
  };

  const handleReplyClick = (message) => {
    setReplyingTo(message);
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
  };

  useEffect(() => {
    if (!scrollRef.current) return;
    if (userScrolled.current) {
      if (messages.length > 0) setShowNewMessages(true);
    } else {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }
  }, [messages]);

  useLayoutEffect(() => {
    if (scrollRef.current && initialLoad.current && messages.length > 0) {
      setTimeout(() => {
        scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "auto" });
        initialLoad.current = false;
      }, 0);
    }
  }, [messages]);

  return (
    <div className={`chat-container kick ${active ? "active" : ""}`} id={username}>
      <div className="chat-banner">
        <p>{username}</p>
      </div>

      <div className="kickframe">
        <div className="kick-chat-header">Chat</div>
        <div ref={scrollRef} onScroll={handleScroll} className="kickchat-scroll">
          {/* ⭐ MODERATION: User Overview Panel */}
          {selectedUser && (
            <KickUserPanel
              user={selectedUser}
              canBan={canBanScope}
              canDelete={canDeleteScope}
              lastMessageId={lastDeletableMessageId(chat, selectedUser.id)}
              enableUrl={kickModerationLoginUrl()}
              onAction={handleModAction}
              onClose={closeUserPanel}
            />
          )}
          
          {messages.map((m, index) => (
            <KickFeedItem
              key={kickFeedItemKey(m, index)}
              message={m}
              currentUserId={currentUserId}
              currentUsername={currentUsername}
              channelSubBadges={channelSubBadges}
              showModeration={canSeeModerationMessages()}
              canModerateUser={canModerateSender(m)}
              canDelete={canDeleteScope && canModerateSender(m)}
              onUserClick={handleUserClick}
              onReply={handleReplyClick}
              onDelete={(msg) => handleModAction('delete', msg.sender?.id, msg.id)}
            />
          ))}
        </div>

        {showNewMessages && (
          <button className="new-messages-btn" onClick={scrollToBottom}>
            New messages ↓
          </button>
        )}

        {showConnectionStatus && <KickConnectionBanner status={connectionStatus} onReconnect={reconnect} />}
      </div>

      {broadcasterId && (
        <KickChatPost 
          broadcasterId={broadcasterId} 
          slug={username}
          replyingTo={replyingTo}
          onCancelReply={handleCancelReply}
          identity={currentUserIdentity}
          identityLoading={identityLoading}
          restriction={viewerRestriction}
          recentChatters={recentChatters}
          moderationScopesMissing={canModerate() && (!canBanScope || !canDeleteScope)}
        />
      )}

    </div>
  );
});

export default KickChatComponent;